import { useEffect } from 'react';
import {
  startSpanOnActiveTransaction,
  finishSpan,
} from '@leagueplatform/observability';

interface VisibleNode {
  nodeId: string;
  ttvMs: number;
}

/**
 * The reason for flushing the span.
 * - settled: All nodes have resolved and the span is being flushed.
 * - timeout: The span is being flushed due to a timeout.
 * - unmount: The span is being flushed due to the component unmounting.
 */
type SpanFlushReason = 'settled' | 'timeout' | 'unmount';

// The timeout for the span.
const VISIBILITY_TIMEOUT_MS = 20_000;

const ABOVE_FOLD_THRESHOLD_PX = window?.innerHeight;

// After the last tracked element resolves, wait this long before emitting.
export const SPAN_DEBOUNCE_MS = 10_000;

// Tracks Time-to-Visible (TTV) for DOM elements that render above the fold.
export const useTTVTracking = (): void => {
  useEffect(() => {
    const startTime = performance.now();
    const route = window.location.pathname;

    // Masonry nodes discovered in the DOM that are still in skeleton state (aria-busy present).
    // Position is intentionally not checked yet — skeleton dimensions can differ
    // from the real content, making fold detection unreliable at this stage.
    const awaitingContent = new Set<Element>();

    // Above-fold elements that have been discovered but not yet through IntersectionObserver.
    const awaitingVisibility = new Set<Element>();

    // Guards against processing the same element reference more than once.
    const seenElements = new Set<Element>();

    // Elements that have been through IntersectionObserver and are considered above the fold.
    const visibleNodes: VisibleNode[] = [];

    let spanSent = false;
    let timeoutId: ReturnType<typeof setTimeout>;
    let pendingSendTimer: ReturnType<typeof setTimeout> | undefined;

    const clearPendingSend = () => {
      if (pendingSendTimer !== undefined) {
        clearTimeout(pendingSendTimer);
        pendingSendTimer = undefined;
      }
    };

    const sendSpan = (flushReason: SpanFlushReason) => {
      if (spanSent || visibleNodes.length === 0) return;
      // Timeout may emit while skeletons or IO are still pending; settled/unmount
      // only when nothing left to track (same bar as scheduling debounced send).
      if (
        flushReason !== 'timeout' &&
        (awaitingContent.size > 0 || awaitingVisibility.size > 0)
      )
        return;

      spanSent = true;
      clearTimeout(timeoutId);
      clearPendingSend();

      const maxTtvMs = Math.max(...visibleNodes.map((n) => n.ttvMs));
      const nowMs = Date.now();
      const nowPerf = performance.now();

      const spanDetails = {
        op: 'measurements.time-to-view',
        group: route,
        description: `${route} above the fold - time to visible`,
        // Back-date the span start to hook mount time so the span duration equals TTV.
        startTimestamp: (nowMs - (nowPerf - startTime)) / 1000,
        data: {
          value: maxTtvMs,
          unit: 'millisecond',
          nodes: visibleNodes,
          flushReason,
          viewportHeightPx: ABOVE_FOLD_THRESHOLD_PX,
        },
      };

      const span = startSpanOnActiveTransaction(spanDetails);
      if (span) finishSpan(span);
    };

    // Checks if the span should be sent based on the current state of the tracking sets.
    const checkSend = () => {
      clearPendingSend();

      if (awaitingContent.size === 0 && awaitingVisibility.size === 0) {
        if (visibleNodes.length === 0) return;

        pendingSendTimer = setTimeout(() => {
          pendingSendTimer = undefined;
          sendSpan('settled');
        }, SPAN_DEBOUNCE_MS);
      }
    };

    const intersectionObserver = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          // One observation per element — unobserve immediately so scroll events
          // never trigger a second measurement.
          intersectionObserver.unobserve(entry.target);
          awaitingVisibility.delete(entry.target);

          if (entry.isIntersecting) {
            const nodeId = entry.target.getAttribute('data-testid') ?? '';
            const ttvMs = Math.round(performance.now() - startTime);
            visibleNodes.push({ nodeId, ttvMs });
          }

          checkSend();
        });
      },
      { threshold: 0 },
    );

    // Checks the element's position against the fold threshold and observes it if it qualifies.
    const checkPositionAndObserve = (el: Element) => {
      const absoluteTop = el.getBoundingClientRect().top + window.scrollY;
      if (absoluteTop >= ABOVE_FOLD_THRESHOLD_PX) return;
      awaitingVisibility.add(el);
      intersectionObserver.observe(el);
    };

    // Entry point for every element discovered in the DOM.
    const trackNode = (el: Element) => {
      if (seenElements.has(el)) return;
      seenElements.add(el);

      if (el.hasAttribute('aria-busy')) {
        // Element is in skeleton state — hold in awaitingContent until content loads.
        awaitingContent.add(el);
      } else {
        // Element is not in skeleton state — check if it is above the fold and observe it if it is.
        checkPositionAndObserve(el);
      }
    };

    // Pick up any masonry nodes already in the DOM when the hook mounts.
    document.querySelectorAll('[data-testid*="$"]').forEach(trackNode);

    const mutationObserver = new MutationObserver((mutations) => {
      const shouldEvaluateSend = mutations.some(
        (m) =>
          m.type === 'childList' ||
          (m.type === 'attributes' && m.attributeName === 'aria-busy'),
      );

      mutations.forEach((mutation) => {
        if (mutation.type === 'childList') {
          Array.from(mutation.removedNodes).forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const el = node as Element;
              awaitingContent.delete(el);
              awaitingVisibility.delete(el);
              intersectionObserver.unobserve(el);
            }
          });

          Array.from(mutation.addedNodes).forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) {
              const el = node as Element;
              trackNode(el);
            }
          });
        } else if (
          mutation.type === 'attributes' &&
          mutation.attributeName === 'aria-busy'
        ) {
          // Handles the less common case where masonry replaces skeleton elements with new content elements rather than updating them in place (aria-busy removed on the existing element).
          const el = mutation.target as Element;
          if (awaitingContent.has(el) && !el.hasAttribute('aria-busy')) {
            awaitingContent.delete(el);
            checkPositionAndObserve(el);
          }
        }
      });

      if (shouldEvaluateSend) {
        checkSend();
      }
    });

    // Page content is either a masonry root node, the main content element, or the body, depending on the page.
    const pageContent =
      document.querySelector('[data-testid*="$root"]') ||
      document.getElementById('main-content') ||
      document.body;

    mutationObserver.observe(pageContent, {
      childList: true,
      subtree: true,
      attributes: true,
    });

    timeoutId = setTimeout(() => sendSpan('timeout'), VISIBILITY_TIMEOUT_MS);

    return () => {
      clearTimeout(timeoutId);
      clearPendingSend();
      sendSpan('unmount');
      intersectionObserver.disconnect();
      mutationObserver.disconnect();
    };
  }, []);
};
